import { useContext, useState } from 'react'

import {
  ActionIcon,
  Drawer,
  Divider,
  Text,
  Menu,
  Button as MantineButton,
  Group,
  Tooltip,
} from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { RecruitersContext } from 'src/context/RecruitersContext'
import {
  BULK_UPDATE_JOB_LISTING_EMPLOYEES,
  GET_JOB_LISTINGS,
} from 'src/graphql/joblistings.graphql'
import IconAdd from 'src/icons/IconAdd'
import IconClose from 'src/icons/IconClose'
import IconUsers from 'src/icons/IconUsers'

import type { SelectedJobListing } from '../JobsListingTable/JobsListingTable'

type EmployeeOption = {
  label: string
  value: string
  status?: string | null
}

interface BulkJobListingEmployeeAssignmentDrawerProps {
  opened: boolean
  onClose: () => void
  selectedJobListings: SelectedJobListing[]
  setSelectedJobListings: React.Dispatch<
    React.SetStateAction<SelectedJobListing[]>
  >
}

const BulkJobListingEmployeeAssignmentDrawer = ({
  opened,
  onClose,
  selectedJobListings,
  setSelectedJobListings,
}: BulkJobListingEmployeeAssignmentDrawerProps) => {
  const recruiters = useContext(RecruitersContext) as EmployeeOption[]
  const [leadEmployees, setLeadEmployees] = useState<EmployeeOption[]>([])
  const [memberEmployees, setMemberEmployees] = useState<EmployeeOption[]>([])

  const [bulkUpdateJobListingEmployees, { loading }] = useMutation(
    BULK_UPDATE_JOB_LISTING_EMPLOYEES,
    {
      refetchQueries: [GET_JOB_LISTINGS],
      onCompleted: (data) => {
        const results = data?.bulkUpdateJobListingEmployees?.results || []
        const successCount = results.filter((r) => r.success).length
        const failureCount = results.filter((r) => !r.success).length

        if (failureCount === 0) {
          toast(
            `Successfully updated employees for ${successCount} job listing${successCount !== 1 ? 's' : ''}`,
            'success'
          )
        } else if (successCount === 0) {
          toast('Failed to update job listings', 'error')
        } else {
          toast(
            `Updated ${successCount} job listing${successCount !== 1 ? 's' : ''}, ${failureCount} failed`,
            'warning'
          )
        }

        handleClose()
      },
      onError: (error) => {
        console.error('Bulk update error:', error)
        toast('Unable to update job listings, please try again.', 'error')
      },
    }
  )

  const handleClose = () => {
    setLeadEmployees([])
    setMemberEmployees([])
    setSelectedJobListings([])
    onClose()
  }

  const handleSubmit = () => {
    bulkUpdateJobListingEmployees({
      variables: {
        input: {
          jobListingIds: selectedJobListings.map((jl) => jl.jobListingId),
          leadEmployeeIds: leadEmployees.map((e) => e.value),
          memberEmployeeIds: memberEmployees.map((e) => e.value),
        },
      },
    })
  }

  const handleRemoveJobListing = (jobListingId: string) => {
    setSelectedJobListings((prev) =>
      prev.filter((jl) => jl.jobListingId !== jobListingId)
    )
  }

  const leadEmployeeIds = leadEmployees.map((e) => e.value)
  const memberEmployeeIds = memberEmployees.map((e) => e.value)

  const availableLeadEmployeeOptions = recruiters.filter(
    (employee) =>
      !leadEmployeeIds.includes(employee.value) &&
      !memberEmployeeIds.includes(employee.value)
  )

  const availableMemberEmployeeOptions = recruiters.filter(
    (employee) =>
      !memberEmployeeIds.includes(employee.value) &&
      !leadEmployeeIds.includes(employee.value)
  )

  const handleLeadEmployeeAdd = (employeeId: string) => {
    const employee = recruiters.find((r) => r.value === employeeId)
    if (employee) {
      setLeadEmployees((prev) => [...prev, employee])
      // Remove from members if they were there
      setMemberEmployees((prev) => prev.filter((e) => e.value !== employeeId))
    }
  }

  const handleMemberEmployeeAdd = (employeeId: string) => {
    // Don't add if already a lead
    if (leadEmployeeIds.includes(employeeId)) {
      return
    }
    const employee = recruiters.find((r) => r.value === employeeId)
    if (employee) {
      setMemberEmployees((prev) => [...prev, employee])
    }
  }

  const isDisabled =
    selectedJobListings.length === 0 ||
    (leadEmployees.length === 0 && memberEmployees.length === 0)

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      title="Bulk Assign Employees"
      classNames={{
        title: 'text-base font-semibold text-doubleNickel-gray-900',
        content: 'flex flex-col h-full',
        body: 'flex flex-col flex-1 px-4 pb-4 overflow-hidden',
      }}
      position="right"
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
    >
      <div className="flex h-full flex-col gap-4 overflow-auto">
        <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
          {`${selectedJobListings.length} ${selectedJobListings.length === 1 ? 'JOB LISTING' : 'JOB LISTINGS'}`}
        </Text>

        {/* Selected Job Listings List */}
        <div className="flex max-h-[200px] flex-col gap-2 overflow-y-auto rounded border border-doubleNickel-gray-200 p-2">
          {selectedJobListings.map((jobListing) => (
            <div
              key={jobListing.jobListingId}
              className="flex items-center justify-between rounded bg-doubleNickel-gray-50 px-2 py-1.5"
            >
              <span className="truncate text-xs text-doubleNickel-gray-900">
                {jobListing.title}
              </span>
              <ActionIcon
                variant="subtle"
                color="gray"
                size={20}
                onClick={() => handleRemoveJobListing(jobListing.jobListingId)}
                disabled={loading}
              >
                <IconClose className="h-3.5 fill-none stroke-doubleNickel-gray-500" />
              </ActionIcon>
            </div>
          ))}
        </div>

        <Divider variant="dashed" />

        <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
          EMPLOYEE ASSIGNMENT
        </Text>

        {/* Lead Employees Section */}
        <div className="flex w-full flex-col gap-1">
          <div className="text-[11px] font-medium text-doubleNickel-gray-500">
            Lead Employees ({leadEmployees.length})
          </div>
          <div className="flex w-full flex-col gap-1.5">
            {leadEmployees.length === 0 && (
              <div className="rounded border border-doubleNickel-gray-200 bg-white px-2 py-1.5 text-xs text-doubleNickel-gray-500">
                No lead employees selected
              </div>
            )}
            {leadEmployees.map((employee) => (
              <div
                key={employee.value}
                className="flex items-center justify-between rounded border border-doubleNickel-gray-200 bg-white px-2 py-1.5"
              >
                <span className="text-xs text-doubleNickel-gray-900">
                  {employee.label}
                </span>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size={20}
                  onClick={() =>
                    setLeadEmployees((prev) =>
                      prev.filter((e) => e.value !== employee.value)
                    )
                  }
                  disabled={loading}
                >
                  <IconClose className="h-3.5 fill-none stroke-doubleNickel-gray-500" />
                </ActionIcon>
              </div>
            ))}
            <Menu shadow="md" position="bottom-start" width="target">
              <Menu.Target>
                <MantineButton
                  variant="subtle"
                  color="gray"
                  size="xs"
                  className="w-full justify-center border border-dashed border-doubleNickel-gray-300 text-doubleNickel-gray-500 hover:bg-doubleNickel-gray-50"
                  leftSection={
                    <IconAdd className="h-3 w-3 stroke-doubleNickel-gray-500" />
                  }
                  disabled={loading}
                >
                  Add Lead
                </MantineButton>
              </Menu.Target>
              <Menu.Dropdown className="max-h-[300px] overflow-y-auto">
                {availableLeadEmployeeOptions.length === 0 ? (
                  <Menu.Item disabled>No employees available</Menu.Item>
                ) : (
                  availableLeadEmployeeOptions.map((employee) => (
                    <Menu.Item
                      key={employee.value}
                      onClick={() => handleLeadEmployeeAdd(employee.value)}
                    >
                      {employee.label}
                    </Menu.Item>
                  ))
                )}
              </Menu.Dropdown>
            </Menu>
          </div>
        </div>

        {/* Member Employees Section */}
        <div className="flex w-full flex-col gap-1">
          <div className="text-[11px] font-medium text-doubleNickel-gray-500">
            Members ({memberEmployees.length})
          </div>
          <div className="flex w-full flex-col gap-1.5">
            {memberEmployees.map((member) => (
              <div
                key={member.value}
                className="flex items-center justify-between rounded border border-doubleNickel-gray-200 bg-white px-2 py-1.5"
              >
                <span className="text-xs text-doubleNickel-gray-900">
                  {member.label}
                </span>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size={20}
                  onClick={() =>
                    setMemberEmployees((prev) =>
                      prev.filter((e) => e.value !== member.value)
                    )
                  }
                  disabled={loading}
                >
                  <IconClose className="h-3.5 fill-none stroke-doubleNickel-gray-500" />
                </ActionIcon>
              </div>
            ))}
            <Menu shadow="md" position="bottom-start" width="target">
              <Menu.Target>
                <MantineButton
                  variant="subtle"
                  color="gray"
                  size="xs"
                  className="w-full justify-center border border-dashed border-doubleNickel-gray-300 text-doubleNickel-gray-500 hover:bg-doubleNickel-gray-50"
                  leftSection={
                    <IconAdd className="h-3 w-3 stroke-doubleNickel-gray-500" />
                  }
                  disabled={loading}
                >
                  Add Member
                </MantineButton>
              </Menu.Target>
              <Menu.Dropdown className="max-h-[300px] overflow-y-auto">
                {availableMemberEmployeeOptions.length === 0 ? (
                  <Menu.Item disabled>No employees available</Menu.Item>
                ) : (
                  availableMemberEmployeeOptions.map((employee) => (
                    <Menu.Item
                      key={employee.value}
                      onClick={() => handleMemberEmployeeAdd(employee.value)}
                    >
                      {employee.label}
                    </Menu.Item>
                  ))
                )}
              </Menu.Dropdown>
            </Menu>
          </div>
        </div>

        <Group className="mt-auto grid grid-cols-2">
          <Button
            text="Cancel"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          />
          <Tooltip
            disabled={!isDisabled}
            label="Please select at least one job listing and one employee"
          >
            <Button
              text={`Assign to ${selectedJobListings.length} job listing${selectedJobListings.length !== 1 ? 's' : ''}`}
              variant="filled"
              lefticon={<IconUsers />}
              disabled={isDisabled}
              loading={loading}
              onClick={handleSubmit}
            />
          </Tooltip>
        </Group>
      </div>
    </Drawer>
  )
}

export default BulkJobListingEmployeeAssignmentDrawer
